export type AIContextSnapshot = {
  screenId: string;
  screenLabel: string;
  scopeType: "overview" | "channels" | "campaigns" | "generic";
  visibleMetrics: string[];
  activeFilters: string[];
  suggestedRoute: string | null;
  contextHint: string;
  suggestedPrompts: string[];
  readinessLabel: string;
};

function normalizePathname(pathname: string) {
  return pathname.replace(/\/+$/, "") || "/";
}

function buildHealthSnapshot(params: {
  screenId: string;
  screenLabel: string;
  route: string;
  serviceLabel: string;
  focusLabel: string;
  metrics: string[];
  prompts: string[];
}) {
  return {
    screenId: params.screenId,
    screenLabel: params.screenLabel,
    scopeType: "overview" as const,
    visibleMetrics: params.metrics,
    activeFilters: ["periodo", "empresa", "canal", "secao"],
    suggestedRoute: params.route,
    contextHint: `Dashboard de ${params.serviceLabel} com foco em ${params.focusLabel}, leitura executiva por período e comparação assistencial dentro do escopo autorizado.`,
    suggestedPrompts: params.prompts,
    readinessLabel: `Contexto analítico de ${params.serviceLabel} carregado`,
  };
}

export function buildAIContextSnapshot(pathname: string): AIContextSnapshot {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === "/dashboards/canais") {
    return {
      screenId: "nr1_channels_dashboard",
      screenLabel: "Dashboard — Performance Canais",
      scopeType: "channels",
      visibleMetrics: [
        "ranking_de_canais",
        "total_clientes_por_canal",
        "total_avaliacoes_por_canal",
        "avaliacoes_concluidas",
        "pipeline_por_canal",
        "taxa_de_conclusao",
        "documentos_prontos",
      ],
      activeFilters: [],
      suggestedRoute: normalizedPath,
      contextHint: "Tela orientada a comparação entre canais, com foco em ranking, volume operacional, pipeline e taxa de conclusão.",
      suggestedPrompts: [
        "Qual canal lidera em taxa de conclusão agora?",
        "Quais canais concentram mais avaliações pendentes?",
        "Existe algum canal com volume alto e baixa conversão?",
      ],
      readinessLabel: "Contexto comparativo carregado",
    };
  }

  if (normalizedPath === "/home/salvia") {
    return {
      screenId: "nr1_overview_home",
      screenLabel: "Visão Geral do NR1",
      scopeType: "overview",
      visibleMetrics: [
        "cards_nr1",
        "pipeline_operacional",
        "distribuicao_de_riscos",
        "distribuicao_de_qualificadores",
        "avaliacoes_recentes",
        "documentos_gerados",
      ],
      activeFilters: [],
      suggestedRoute: normalizedPath,
      contextHint: "Tela executiva do NR1 com leitura consolidada do funil operacional, dos riscos, dos qualificadores e das avaliações recentes.",
      suggestedPrompts: [
        "Quais são os principais pontos de atenção do NR1 agora?",
        "Onde está o maior gargalo do pipeline operacional?",
        "Os indicadores desta visão sugerem risco elevado em alguma frente?",
      ],
      readinessLabel: "Contexto executivo carregado",
    };
  }

  if (normalizedPath === "/portal/salvia") {
    return {
      screenId: "insights_hub",
      screenLabel: "Insights com IA",
      scopeType: "generic",
      visibleMetrics: ["modulos_visiveis", "sumarios_autorizados", "submissoes_no_escopo", "campanhas_elegiveis"],
      activeFilters: [],
      suggestedRoute: normalizedPath,
      contextHint: "Hub transversal do portal com foco em visão autorizada, disponibilidade de módulos e sinais executivos agregados.",
      suggestedPrompts: [
        "Quero uma visão geral da plataforma com base no contexto autorizado atual.",
        "Quais módulos estão visíveis e operáveis para este contexto?",
        "Quais conclusões globais do NR1 já podem ser extraídas agora?",
      ],
      readinessLabel: "Contexto transversal carregado",
    };
  }

  if (normalizedPath.startsWith("/portal/servicos/telemedicina")) {
    return buildHealthSnapshot({
      screenId: "health_telemedicina_dashboard",
      screenLabel: "Dashboard — Telemedicina",
      route: normalizedPath,
      serviceLabel: "Telemedicina",
      focusLabel: "produção médica, eficiência, no-show e experiência do cuidado remoto",
      metrics: ["atendimentos", "agendamentos", "conversao", "no_show", "experiencia", "risco"],
      prompts: [
        "Quais sinais da telemedicina merecem atenção imediata neste período?",
        "A eficiência da telemedicina está coerente com o volume atendido?",
        "Existe algum indício de gargalo, no-show ou risco clínico mais relevante aqui?",
      ],
    });
  }

  if (normalizedPath.startsWith("/portal/servicos/aps-fisica")) {
    return buildHealthSnapshot({
      screenId: "health_aps_fisica_dashboard",
      screenLabel: "Dashboard — APS Física",
      route: normalizedPath,
      serviceLabel: "APS Física",
      focusLabel: "produção assistencial, risco populacional, engajamento e continuidade do cuidado presencial",
      metrics: ["vidas_elegiveis", "producao", "especialidades", "busca_ativa", "monitoramento", "experiencia"],
      prompts: [
        "Quais blocos da APS Física mais merecem atenção executiva agora?",
        "A produção da APS Física está coerente com o tamanho da base elegível?",
        "Quais oportunidades de risco, experiência ou busca ativa aparecem neste módulo?",
      ],
    });
  }

  if (normalizedPath.startsWith("/portal/servicos/aps-digital")) {
    return buildHealthSnapshot({
      screenId: "health_aps_digital_dashboard",
      screenLabel: "Dashboard — APS Digital",
      route: normalizedPath,
      serviceLabel: "APS Digital",
      focusLabel: "adesão digital, produção, busca ativa, risco e experiência no cuidado longitudinal",
      metrics: ["vidas_elegiveis", "producao", "funil_de_acesso", "especialidades", "busca_ativa", "nps"],
      prompts: [
        "O que a APS Digital revela de mais crítico neste recorte?",
        "Quais sinais de adesão e produção estão mais fortes ou mais frágeis agora?",
        "Se eu fosse priorizar uma ação executiva neste módulo, qual deveria ser?",
      ],
    });
  }

  return {
    screenId: "nr1_generic_surface",
    screenLabel: "Superfície genérica do NR1",
    scopeType: "generic",
    visibleMetrics: ["contexto_autorizado_nr1"],
    activeFilters: [],
    suggestedRoute: null,
    contextHint: "Superfície genérica do NR1 para leituras orientadas ao contexto autorizado disponível na navegação atual.",
    suggestedPrompts: [
      "O que esta área do NR1 sugere como próximo foco?",
      "Quais sinais já consigo interpretar nesta tela?",
      "Se eu quiser aprofundar, qual dashboard devo abrir?",
    ],
    readinessLabel: "Contexto básico carregado",
  };
}
